import { DragEvent, FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ConnectedProductCard } from '@telialt/core-search/src/Common/ConnectedProductCard/ConnectedProductCard';
import { PageChangeHandler } from '@telialt/core-search/src/Common/Pagination/model';
import { Pagination } from '@telialt/core-search/src/Common/Pagination/Pagination';
import { SEARCH_CATEGORY_PRODUCTS_RESULTS_PER_PAGE_COUNT } from '@telialt/core-search/src/constants';
import { ProductSearchItem, SearchQueryResponse } from '@telialt/core-search/src/model';
import { getResultsPathWithPage } from '@telialt/core-search/src/utils/paths';
import { ProductsCompareProduct } from '@telialt/eshop-comparison/src/models';
import { withParams } from '@telialt/utils/src/helpers';
import { getComparisonPagePath } from '@telialt/utils/src/pathUtils';
import { TeliaSegment } from '@telialt/utils/src/segment';

import { PaginationContainer, ProductsGrid } from './ProductsItems.styles';

type Props = {
    segment: TeliaSegment;
    categoryPath: string;
    offset: number;
    pageNumber: number;
    products: SearchQueryResponse<ProductSearchItem>;
    currentPathname: string;
    currentSearchParams: URLSearchParams;
    onPageChange: (page: number) => void;
    hasComparison: boolean;
    comparedProducts: ProductsCompareProduct[];
    compareProductsLimit: number;
    onCompare: (product: ProductSearchItem, isChecked: boolean) => void;
    onDragStart: (event: DragEvent, product: ProductSearchItem) => void;
    onItemClick: (product: ProductSearchItem, index: number) => void;
};

export const ProductsItems: FC<Props> = ({
    segment,
    categoryPath,
    offset,
    pageNumber,
    products,
    currentPathname,
    currentSearchParams,
    onPageChange,
    hasComparison,
    comparedProducts,
    compareProductsLimit,
    onCompare,
    onDragStart,
    onItemClick,
}) => {
    const { t } = useTranslations();

    const handlePageChange: PageChangeHandler = (_limitPerPage, page: number) => onPageChange(page);

    return (
        <div>
            <ProductsGrid role="list">
                {products?.items?.map((product, index) => {
                    const isCompareChecked = !!comparedProducts.some((item) => item.id === product.id);

                    return (
                        <div
                            key={product.id}
                            role="listitem"
                            {...(hasComparison && {
                                draggable: true,
                                onDragStart: (event) => onDragStart(event, product),
                            })}
                        >
                            <ConnectedProductCard
                                key={product.id}
                                product={product}
                                isCompareChecked={isCompareChecked}
                                isCompareDisabled={comparedProducts.length >= compareProductsLimit && !isCompareChecked}
                                onCompare={hasComparison ? (isChecked) => onCompare(product, isChecked) : undefined}
                                compareDisabledMessage={withParams(t['eshop.category.compare.limit'], {
                                    comparePath: getComparisonPagePath(segment, categoryPath),
                                    maxCount: compareProductsLimit,
                                })}
                                onClick={() => onItemClick(product, index)}
                            />
                        </div>
                    );
                })}
            </ProductsGrid>
            <PaginationContainer>
                <Pagination
                    offset={offset}
                    pageLimit={SEARCH_CATEGORY_PRODUCTS_RESULTS_PER_PAGE_COUNT}
                    pageNumber={pageNumber}
                    totalCount={products?.total ?? 0}
                    getPageLink={(page) => getResultsPathWithPage(currentPathname, currentSearchParams, page)}
                    onPageChange={handlePageChange}
                />
            </PaginationContainer>
        </div>
    );
};
